import axiosStatic, {
  AxiosHeaders,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios'
import { nanoid } from 'nanoid'

import { ScriptoApiClient } from '@showrunner/legacy-client'

import { getTokenHeader } from '#util/authToken'
import { SCRIPTO_REQUEST_ID_HEADER } from '#util/constants'
import { DatadogClient } from '#util/datadog'
import { handle401 } from '#util/handle401'

type RequestDetails = {
  method: string
  path: string
  reqId: string
  startTime: number
}

const requestDetails = new WeakMap<InternalAxiosRequestConfig, RequestDetails>()

function configureTransport(
  axios: AxiosInstance,
  options: { includeAuth: boolean },
): AxiosInstance {
  const ddlog = DatadogClient.getInstance()

  axios.interceptors.request.use((config) => {
    const reqId = nanoid()
    const headers = AxiosHeaders.from(config.headers)
    const tokenHeader = options.includeAuth ? getTokenHeader() : undefined

    if (tokenHeader) {
      headers.set(tokenHeader)
    }
    headers.set(SCRIPTO_REQUEST_ID_HEADER, reqId)
    config.headers = headers
    requestDetails.set(config, {
      method: config.method ?? 'get',
      path: config.url ?? 'unknown',
      reqId,
      startTime: Date.now(),
    })
    return config
  })

  axios.interceptors.response.use(
    (response) => {
      const details = requestDetails.get(response.config)
      if (details) {
        ddlog.logApiRequest({ ...details, status: response.status })
      }
      return response
    },
    async (error: unknown) => {
      const axiosError = axiosStatic.isAxiosError(error) ? error : undefined
      const config = axiosError?.config
      const details = config ? requestDetails.get(config) : undefined
      const status = axiosError?.response?.status ?? 0

      if (details) {
        ddlog.logApiRequest({ ...details, status, error })
      }
      if (status === 401) {
        await handle401()
      }
      throw error
    },
  )

  return axios
}

export function createScriptoApiClient(axios: AxiosInstance): ScriptoApiClient {
  const directTransport = configureTransport(axios, { includeAuth: true })
  const proxyTransport = configureTransport(
    axiosStatic.create(axios.defaults),
    { includeAuth: false },
  )

  return new ScriptoApiClient({
    axios: directTransport,
    proxyAxios: proxyTransport,
    proxyBaseUrl: window.location.origin + '/api/v0',
  })
}
